import { useEffect, useRef, useState } from 'react';
import { useEventListener } from 'usehooks-ts';
import useConversationStore from '@/hooks/conversations/use-conversation-store';
import { addTranslateToast } from '@/stores/toast';

export function useAudioControls(url: string) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const documentRef = useRef(document);
  const [isLoading, setLoading] = useState(false);
  const [isPlaying, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [position, setPosition] = useState(0);
  const [isExpired, setIsExpired] = useState(false);

  const { isMediaPlaying, setMediaPlaying, clearMediaPlaying } = useConversationStore();

  useEventListener(
    'visibilitychange',
    () => {
      const isVisible = document.visibilityState;

      if (isVisible === 'hidden') {
        audioRef.current?.pause();
      }
    },
    documentRef,
  );

  useEventListener(
    'waiting',
    () => {
      setLoading(true);
    },
    audioRef,
  );

  useEventListener(
    'playing',
    () => {
      setLoading(false);
      setPlaying(true);
      setMediaPlaying(url);
    },
    audioRef,
  );

  useEventListener(
    'timeupdate',
    () => {
      if (!audioRef.current) return;
      const duration = audioRef.current.duration;
      const position = audioRef.current.currentTime;

      setPosition(position);
      setProgress(Number(((position / duration) * 100).toFixed(2)));
    },
    audioRef,
  );

  useEventListener(
    'pause',
    () => {
      setPlaying(false);
      if (isMediaPlaying === url) clearMediaPlaying();
    },
    audioRef,
  );

  useEventListener(
    'ended',
    () => {
      setPlaying(false);
      if (isMediaPlaying === url) clearMediaPlaying();
    },
    audioRef,
  );

  useEventListener(
    'error',
    (event) => {
      setLoading(false);
      setPlaying(false);

      const audio = event.target as HTMLAudioElement;
      const error = audio.error;

      if (error) {
        if (
          error.code === MediaError.MEDIA_ERR_NETWORK ||
          error.code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED ||
          error.code === MediaError.MEDIA_ERR_ABORTED
        ) {
          addTranslateToast({
            type: 'error',
            message: 'audio.play-failed',
          });
        } else if (error.code === MediaError.MEDIA_ERR_DECODE) {
          setIsExpired(true);
          addTranslateToast({
            type: 'error',
            message: 'audio.expired',
          });
        }
      }
    },
    audioRef,
  );

  useEventListener(
    'canplaythrough',
    () => {
      audioRef.current?.play().catch((error) => {
        if (error.name === 'NotAllowedError' || error.name === 'NotSupportedError') {
          addTranslateToast({
            type: 'error',
            message: 'audio.play-failed',
          });
        }
      });
    },
    audioRef,
  );

  useEffect(() => {
    if (isMediaPlaying !== url) audioRef.current?.pause();
  }, [isMediaPlaying, url]);

  const toggle = () => {
    if (isExpired) {
      addTranslateToast({
        type: 'error',
        message: 'audio.expired',
      });
      return;
    }

    if (isPlaying) {
      audioRef.current?.pause();
    } else {
      audioRef.current?.load();
    }
  };

  return {
    play: () => {
      if (isExpired) {
        addTranslateToast({
          type: 'error',
          message: 'audio.expired',
        });
        return;
      }
      audioRef.current?.load();
    },
    pause: () => audioRef.current?.pause(),
    toggle,
    isPlaying,
    isLoading,
    isExpired,
    progress,
    position,
    ref: audioRef,
  } as const;
}
